import { refusedAs, succeeded } from '@crow/service-kit/web/api';
import type { SteerTurnRequest } from '@crowdex/agent-contracts/api';
import { apiResponse } from '@crowdex/host-kit/renderer/http';
import { useCallback, useMemo, useState } from 'react';
import {
  type ActivityEntry,
  needsYouEntries,
  type Waiting,
  waitingByThread,
} from '@/components/mobile-shell/activity-model';
import { useNotifications } from '@/features/notifications/notifications';
import type { SidebarThread } from '@/lib/sidebar-threads';

const NO_WAITING_TYPES: ReadonlySet<string> = new Set();

/** An answer given from a card: an approval's decision, or one of a question's answers. */
export type NeedsYouAnswer =
  | { type: 'approval'; approved: boolean }
  | { type: 'choice'; value: string };

/** `answered`: the agent took it; `stale`: it no longer waits (answered elsewhere, turn ended). */
export type NeedsYouOutcome = 'answered' | 'stale';

export type NeedsYou = {
  /** What waits on the user across workspaces, newest first. */
  entries: ActivityEntry[];
  /** Each conversation's unread waiting event, by conversation id. */
  waiting: ReadonlyMap<string, Waiting>;
  /** Answers a card without opening its conversation; the card is read once the agent took it. */
  answer(entry: ActivityEntry, answer: NeedsYouAnswer): Promise<NeedsYouOutcome>;
};

/** Sends an answer to the agent's turn; false when the request no longer waits. */
async function sendNeedsYouAnswer(
  conversationId: string,
  waiting: Waiting,
  answer: NeedsYouAnswer,
): Promise<boolean> {
  // An app's request is answered at the core, which tells the app.
  const appRequest =
    answer.type === 'approval'
      ? waiting.approval?.turnId === null
        ? { requestId: waiting.approval.requestId, body: { approved: answer.approved } }
        : null
      : waiting.question?.turnId === null
        ? { requestId: waiting.question.requestId, body: { answer: answer.value } }
        : null;
  if (appRequest !== null) {
    return await succeeded(
      apiResponse(`/api/requests/${appRequest.requestId}/answer`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(appRequest.body),
      }),
    );
  }
  // The agent's question tool takes the answer as a message to its turn, as typed in it.
  if (
    answer.type === 'choice' &&
    waiting.question?.turnId != null &&
    waiting.question.field === null
  ) {
    const response = await apiResponse(`/api/agent/turns/${conversationId}/steer`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        turnId: waiting.question.turnId,
        text: answer.value,
        messageId: crypto.randomUUID(),
        // Refused once the question no longer waits (answered on another device).
        request: waiting.question.requestId,
      } satisfies SteerTurnRequest),
    }).catch(refusedAs(null));
    if (response === null) return false;
    const { accepted } = (await response.json().catch(() => ({}))) as { accepted?: boolean };
    return accepted === true;
  }
  const interaction =
    answer.type === 'approval'
      ? waiting.approval === null
        ? null
        : {
            turnId: waiting.approval.turnId,
            interaction: {
              type: 'approval',
              id: waiting.approval.requestId,
              approved: answer.approved,
            },
          }
      : waiting.question === null
        ? null
        : {
            turnId: waiting.question.turnId,
            interaction: {
              type: 'elicitation',
              requestId: waiting.question.requestId,
              outcome: {
                action: 'accept',
                content: { [waiting.question.field ?? 'answer']: answer.value },
              },
            },
          };
  if (interaction === null) return false;
  return await succeeded(
    apiResponse(`/api/agent/turns/${conversationId}/interaction`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(interaction),
    }),
  );
}

/**
 * What waits on the user (an agent's question, an approval, an automation's request), from the
 * conversations' unread events of a type that waits, and how to answer it in place: the phone's Activity and the new
 * conversation's cards read it here. The preview shows the boards' question and approval, and
 * answering one there only takes its card away.
 */
export function useNeedsYou({
  threads,
  preview,
}: {
  threads: readonly SidebarThread[];
  preview: boolean;
}): NeedsYou {
  const notifications = useNotifications();
  const list = notifications?.notifications;
  const [previewAnswered, setPreviewAnswered] = useState<ReadonlySet<string>>(new Set());
  const waitingTypes = notifications?.waitingTypes ?? NO_WAITING_TYPES;
  const waiting = useMemo(
    () =>
      waitingByThread(
        (list ?? []).filter((notification) => !previewAnswered.has(notification.id)),
        waitingTypes,
      ),
    [list, previewAnswered, waitingTypes],
  );
  const entries = useMemo(() => needsYouEntries({ threads, waiting }), [threads, waiting]);
  const answer = useCallback(
    async (entry: ActivityEntry, given: NeedsYouAnswer): Promise<NeedsYouOutcome> => {
      const { waiting: event } = entry;
      if (event === null) return 'stale';
      if (preview) {
        setPreviewAnswered((current) => new Set(current).add(event.notification.id));
        return 'answered';
      }
      const accepted = await sendNeedsYouAnswer(entry.thread.id, event, given);
      // The server settled it with the answer: shown so here at once.
      if (accepted) notifications?.settle([event.notification]);
      return accepted ? 'answered' : 'stale';
    },
    [notifications, preview],
  );
  return { entries, waiting, answer };
}
